import { Controller, TextFieldElement } from '@graphcommerce/ecommerce-ui'
import { iconSearch, IconSvg } from '@graphcommerce/next-ui'
import { i18n } from '@lingui/core'
import { Trans } from '@lingui/react'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import InputAdornment from '@mui/material/InputAdornment'
import NativeSelect from '@mui/material/NativeSelect'
import Typography from '@mui/material/Typography'
import { useStoreLocatorForm } from './StoreLocatorFormProvider'
import { useStores } from './helpers/useStores'

export function StoreFilters() {
  const { control } = useStoreLocatorForm()
  const { stores, loading } = useStores(null)

  const servicesSet = new Set<string>()
  stores?.forEach((store) =>
    store?.services?.forEach((service) => {
      if (service?.label) servicesSet.add(service?.label)
    }),
  )
  const services = [...servicesSet].sort()

  return (
    <>
      <Box>
        <TextFieldElement
          fullWidth
          control={control}
          name='search'
          placeholder={i18n._(/* i18n */ 'Search your store by city, zip code or name')}
          autoComplete='off'
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconSvg src={iconSearch} />
              </InputAdornment>
            ),
          }}
          disabled={loading}
          sx={(theme) => ({
            mb: theme.spacings.xs,

            fieldset: {
              borderWidth: '2px',
            },

            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            },

            '& .MuiInputBase-input': {
              fontSize: '16px',
              paddingLeft: theme.spacings.xs,
            },

            '& .MuiInputBase-root': {
              backgroundColor: theme.palette.background.paper,
              borderRadius: '40px',
              height: '45px',
            },
          })}
        />
      </Box>

      <Box
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: { xs: 0, md: theme.spacings.xs },
        })}
      >
        <Typography
          variant='subtitle1'
          sx={(theme) => ({
            flex: '0 1 auto',
            fontSize: theme.typography.subtitle2,
            fontWeight: '600',
            marginRight: theme.spacings.xs,
            marginBottom: `calc(${theme.spacings.xxs} / 2)`,
          })}
        >
          <Trans id='Show stores with:' />
        </Typography>
        <FormControl sx={{ margin: { xs: 0, md: 0 }, flex: '1 0 auto' }}>
          <Controller
            name='service'
            control={control}
            render={({ field: { value, onChange, onBlur, ref }, fieldState: { error } }) => (
              <NativeSelect
                name='service'
                value={value ?? ''}
                onChange={(evt) => {
                  onChange(evt.target.value)
                }}
                onBlur={onBlur}
                error={Boolean(error)}
                inputRef={ref}
                variant='outlined'
                sx={(theme) => ({
                  '&::after': {
                    display: 'none',
                  },
                  '&::before': {
                    border: '2px solid rgba(0, 0, 0, 0.23)',
                    height: '100%',
                    borderRadius: '40px',
                    transition: 'none',
                  },
                  '&.Mui-disabled::before': {
                    borderBottomStyle: 'solid',
                  },
                  '&:hover::before': {
                    border: '2px solid rgba(0, 0, 0, 0.23)',
                  },
                  '&.Mui-focused::before, &:hover:not(.Mui-disabled):not(.Mui-error)::before': {
                    borderColor: theme.palette.primary.main,
                  },
                  '& .MuiNativeSelect-select': {
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: '40px',
                    height: '45px',
                    py: 0,
                    paddingLeft: theme.spacings.xs,
                    fontSize: '16px',

                    '&:focus': {
                      borderRadius: '40px',
                    },
                  },
                })}
                disabled={loading}
              >
                <option value=''>...</option>
                {services.map((service) => (
                  <option key={service} value={service}>
                    {service}
                  </option>
                ))}
              </NativeSelect>
            )}
          />
        </FormControl>
      </Box>
    </>
  )
}
